import { useRoute } from "preact-iso";
import {
  ArrowRightIcon,
  DocumentTextIcon,
  RocketLaunchIcon,
} from "@heroicons/react/24/outline";
import { api } from "../lib/api-client";
import { adminHref } from "../lib/platform-admin";
import { canManageProject } from "../lib/permissions";
import { useAsyncData } from "../lib/use-async-data";
import { useDeployHook } from "../lib/use-deploy-hook";
import { OwnerBanner } from "../components/owner-banner";
import {
  Alert,
  Button,
  EmptyState,
  ListItem,
  PageHeader,
  SkeletonList,
} from "../components/ui";

// Simple-mode home: just the page list, deploy button, and an escape hatch
// to Advanced. Page creation lives in Advanced — this screen is read-only
// for the project structure, write-only for AI-driven copy edits.
export function PageList() {
  const { params } = useRoute();
  const projectId = params.projectId!;
  const { data, error } = useAsyncData(
    () =>
      Promise.all([
        api.getProject(projectId),
        api.listContents(projectId),
      ]).then(([p, list]) => ({ project: p, items: list.items })),
    [projectId],
  );
  const project = data?.project ?? null;
  const items = data?.items ?? null;
  const { state: deployState, trigger: triggerDeploy } = useDeployHook(project);
  const canManage = canManageProject(project?.viewerRole);

  return (
    <section class="space-y-8">
      <OwnerBanner project={project} />

      <PageHeader
        title={project?.name ?? "..."}
        description={
          <span class="font-mono">/{project?.slug ?? ""}</span>
        }
        actions={
          canManage && (
            <Button
              variant="primary"
              iconLeft={<RocketLaunchIcon class="h-4 w-4" />}
              disabled={!project?.deployHookConfigured}
              loading={deployState.kind === "running"}
              onClick={triggerDeploy}
            >
              {deployState.kind === "running" ? "デプロイ中…" : "デプロイ"}
            </Button>
          )
        }
      />

      {error && <Alert tone="danger">{error}</Alert>}

      {items === null ? (
        <SkeletonList count={3} />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<DocumentTextIcon class="h-5 w-5" />}
          title="ページがまだありません"
          description="新規ページの作成は Advanced モードから行ってください。"
        />
      ) : (
        <ul class="grid gap-2">
          {items.map((ct) => (
            <ListItem key={ct.id} class="group">
              <a
                href={adminHref(`/projects/${projectId}/pages/${ct.slug}`)}
                class="flex items-center justify-between gap-4 px-5 py-4"
              >
                <div class="flex min-w-0 items-center gap-3">
                  <span class="inline-flex h-9 w-9 flex-none items-center justify-center rounded-md border border-border bg-bg-subtle text-fg-subtle">
                    <DocumentTextIcon class="h-4 w-4" />
                  </span>
                  <div class="min-w-0">
                    <div class="truncate text-sm font-medium text-fg">
                      {ct.title}
                    </div>
                    <div class="mt-0.5 truncate font-mono text-xs text-fg-subtle">
                      /{ct.slug}
                    </div>
                  </div>
                </div>
                <ArrowRightIcon class="h-4 w-4 flex-none text-fg-faint transition group-hover:translate-x-0.5 group-hover:text-fg" />
              </a>
            </ListItem>
          ))}
        </ul>
      )}
    </section>
  );
}
